import React, { useContext, useState, useEffect, useCallback } from 'react';
import './cartItems.css';
import { ShopContext } from '../../Context/ShopContext';
import remove_icon from '../Assets/close_icon.png';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const CartItems = () => {
  const {
    cartItems,
    getCartProductDetails,
    removeFromCart,
    updateCartItemQuantity,
    getTotalCartItems,
    validateToken
  } = useContext(ShopContext);

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const checkAuthStatus = useCallback(() => {
    const token = localStorage.getItem('auth-token');
    const isValid = token ? validateToken() : false;
    setIsLoggedIn(isValid);
    return isValid;
  }, [validateToken]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    const handleAuthChange = () => {
      checkAuthStatus();
    };

    checkAuthStatus();
    window.addEventListener('storage', handleAuthChange);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('storage', handleAuthChange);
    };
  }, [checkAuthStatus]);

  const cartProducts = React.useMemo(() => {
    return Object.entries(cartItems || {})
      .map(([compositeId, quantity]) => {
        const productDetails = getCartProductDetails(compositeId);
        return productDetails ? { 
          ...productDetails, 
          quantity: Math.min(quantity, 10),
          compositeId,
          newPrice: productDetails.newPrice || 0
        } : null;
      })
      .filter(Boolean);
  }, [cartItems, getCartProductDetails]);

  const totalAmount = React.useMemo(() => {
    return cartProducts.reduce((total, item) => {
      return total + (item.newPrice * item.quantity);
    }, 0);
  }, [cartProducts]);

  const handleQuantityChange = async (compositeId, newQuantity) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      if (!checkAuthStatus()) {
        toast.info('Please login to modify your cart');
        navigate('/login', { state: { from: location.pathname } });
        return;
      }

      if (newQuantity < 1) {
        handleRemoveItem(compositeId);
        return;
      };
      if (newQuantity > 10) {
        toast.warning('Maximum quantity per item is 10');
        return;
      }
      
      await updateCartItemQuantity(compositeId, newQuantity);
    } catch (error) {
      toast.error('Failed to update quantity');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRemoveItem = async (compositeId) => {
    if (isProcessing) return;
    setIsProcessing(true);
    
    try {
      if (!checkAuthStatus()) {
        toast.info('Please login to modify your cart');
        navigate('/login', { state: { from: location.pathname } });
        return;
      }
      
      const success = await removeFromCart(compositeId);
      if (success) {
        toast.success('Item removed from cart');
      }
    } catch (error) {
      toast.error('Failed to remove item');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCheckout = () => {
    if (!checkAuthStatus()) {
      toast.info('Please login to proceed to checkout');
      navigate('/login', { state: { from: location.pathname } });
      return;
    }
    
    if (cartProducts.length === 0) {
      toast.warning('Your cart is empty');
      return;
    }
    
    navigate('/OrderPayment', {
      state: {
        cartProducts,
        totalAmount: totalAmount.toFixed(2),
        customerName: localStorage.getItem('user-name') || '',
        customerPhone: localStorage.getItem('user-phone') || ''
      }
    });
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading your cart..." />;
  }

  if (!isLoggedIn) {
    return (
      <div className="cartitems-empty">
        <div className="locked-container">
          <h1 className="locked">🔒 Please Login to Access Your Cart</h1>
          <p>You need to be logged in to view and manage your shopping cart</p>
          <Link 
            to="/login" 
            className="empty-cart-button login-prompt"
            state={{ from: location.pathname }}
          >
            Login / Signup
          </Link>
        </div>
      </div>
    );
  }

  if (cartProducts.length === 0) {
    return (
      <div className="cartitems-empty">
        <div className="empty-container">
          <h1>🛒 Your Cart is Empty</h1>
          <p>Looks like you haven't added anything to your cart yet</p>
          <Link to="/" className="empty-cart-button">
            Continue Shopping
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cartitems">
      <div className="cartitems-header">
        <h1 className="cartitems-title">Your Shopping Cart</h1>
        <span className="cartitems-count">
          ({getTotalCartItems()} {getTotalCartItems() === 1 ? 'item' : 'items'})
        </span>
      </div>
      
      <div className="cartitems-container">
        <div className="cartitems-headers">
          <div className="cartitems-header-product">Product</div>
          <div className="cartitems-header-title">Title</div>
          <div className="cartitems-header-price">Price</div>
          <div className="cartitems-header-quantity">Quantity</div>
          <div className="cartitems-header-total">Total</div>
          <div className="cartitems-header-remove">Remove</div>
        </div>
        
        <div className="cartitems-list">
          {cartProducts.map((product) => (
            <div key={product.compositeId} className="cartitems-item">
              <div className="cartitems-product">
                <img 
                  src={product.image} 
                  alt={product.name} 
                  className="cartitems-product-image" 
                  onError={(e) => {
                    e.target.src = '/images/default-product.jpg';
                  }}
                  loading="lazy"
                />
              </div>
              <div className="cartitems-title">
                <Link to={`/product/${product.id}`}>
                  {product.name}
                </Link>
                {(product.selectedSize || product.selectedColor) && (
                  <div className="product-variants">
                    {product.selectedSize && <span>Size: {product.selectedSize}</span>}
                    {product.selectedColor && (
                      <span>
                        Color: <span 
                          className="color-chip" 
                          style={{ backgroundColor: product.selectedColor }}
                        />
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="cartitems-price">
                Ksh. {product.newPrice.toFixed(2)}
              </div>
              <div className="cartitems-quantity">
                <button 
                  onClick={() => handleQuantityChange(product.compositeId, product.quantity - 1)}
                  disabled={isProcessing}
                  aria-label="Decrease quantity"
                >
                  −
                </button>
                <span>{product.quantity}</span>
                <button 
                  onClick={() => handleQuantityChange(product.compositeId, product.quantity + 1)}
                  disabled={product.quantity >= 10 || isProcessing}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <div className="cartitems-total">
                Ksh. {(product.newPrice * product.quantity).toFixed(2)}
              </div>
              <div className="cartitems-remove">
                <button 
                  onClick={() => handleRemoveItem(product.compositeId)}
                  disabled={isProcessing}
                  aria-label="Remove item"
                  className="remove-button"
                >
                  <img 
                    src={remove_icon} 
                    alt="Remove item" 
                    className="remove-icon"
                  />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="cartitems-summary">
        <div className="cartitems-totals">
          <h2>Order Summary</h2>
          <div className="cartitems-summary-row">
            <span>Subtotal ({cartProducts.reduce((acc, item) => acc + item.quantity, 0)} items)</span>
            <span>Ksh. {totalAmount.toFixed(2)}</span>
          </div>
          <div className="cartitems-summary-row">
            <span>Shipping</span>
            <span>Calculated at checkout</span>
          </div>
          <div className="cartitems-summary-row total">
            <span>Total</span>
            <span>Ksh. {totalAmount.toFixed(2)}</span>
          </div>
          <button 
            className="checkout-button" 
            onClick={handleCheckout}
            disabled={cartProducts.length === 0 || isProcessing}
          >
            {isProcessing ? 'Processing...' : 'PROCEED TO CHECKOUT'}
          </button>
          <Link to="/" className="continue-shopping">
            ← Continue Shopping
          </Link>
        </div>
      </div>
      
      <ToastContainer 
        position="bottom-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />
    </div>
  );
};

CartItems.propTypes = {
  // Add prop types validation if needed
};

export default CartItems;